import React, { useMemo, useState } from "react";
import { facilityAdmin } from "../../api";
import { Btn, EmptyState, LoadingSpinner } from "../../components/ui";
import Icon from "../../components/Icons";
import BRAND from "../../brand";
import toast from "react-hot-toast";

const P = BRAND.colorPrimary;
const A = BRAND.colorAccent;
const GOOD = "#16A34A";

// Redesigned Zones & Stations screen. Pure presentation: every action calls
// the same handlers/modals as the classic page (see ZonesPage.js).
export default function ZonesModernView({
  loading, zones, stations, expanded, setExpanded, load,
  setZoneModal, setStationModal, setCounterModal, setOutcomeModal, setMoveModal, setDeleteConfirm,
}) {
  const [query, setQuery] = useState("");
  const [menu, setMenu] = useState(null); // id of the open "..." menu

  const stationsForZone = (zoneId) => stations.filter(s => s.zone === zoneId || s.zone_id === zoneId);
  const countersOf = (st) => st.counters || [];

  const totals = useMemo(() => {
    const counters = stations.flatMap(s => s.counters || []);
    return {
      zones: zones.length,
      stations: stations.length,
      counters: counters.length,
      active: counters.filter(c => c.is_active !== false).length,
    };
  }, [zones, stations]);

  const q = query.trim().toLowerCase();
  const has = (t) => (t || "").toLowerCase().includes(q);
  const stationMatches = (st) => !q || has(st.name) || has(st.code) || countersOf(st).some(c => has(c.name));
  const zoneMatches = (z) => !q || has(z.name) || stationsForZone(z.id).some(stationMatches);

  const toggleCounter = async (counter) => {
    try {
      await facilityAdmin.updateCounter(counter.id, { is_active: counter.is_active === false });
      toast.success(`Counter ${counter.is_active === false ? "activated" : "deactivated"}.`);
      load();
    } catch { toast.error("Failed to update counter."); }
  };

  const Menu = ({ id, items }) => (
    <div style={{ position: "relative" }}>
      <button style={S.iconBtn} onClick={(e) => { e.stopPropagation(); setMenu(menu === id ? null : id); }} title="More">
        <Icon name="more" size={18} stroke={3.2} />
      </button>
      {menu === id && (
        <>
          <div style={S.menuBackdrop} onClick={() => setMenu(null)} />
          <div style={S.menu}>
            {items.map(it => (
              <button key={it.label} style={{ ...S.menuItem, color: it.danger ? "#DC2626" : "var(--text-primary)" }}
                onClick={() => { setMenu(null); it.onClick(); }}>
                <Icon name={it.icon} size={15} /> {it.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const Stat = ({ icon, tint, label, value }) => (
    <div style={{ ...S.card, display: "flex", alignItems: "center", gap: "14px", padding: "16px 18px", background: `linear-gradient(135deg, ${tint}14, transparent 70%), var(--surface-bg)` }}>
      <span style={{ ...S.bubble, width: 46, height: 46, background: `${tint}22`, color: tint }}><Icon name={icon} size={22} /></span>
      <div>
        <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--text-secondary)" }}>{label}</div>
        <div style={{ fontSize: "28px", fontWeight: 700, color: "var(--text-primary)", lineHeight: 1.15 }}>{value}</div>
      </div>
    </div>
  );

  return (
    <div style={S.root}>
      <div style={S.header}>
        <div style={{ display: "flex", gap: "14px", alignItems: "center", minWidth: 0 }}>
          <span style={{ ...S.bubble, width: 52, height: 52, background: `${P}1F`, color: P }}><Icon name="layers" size={26} /></span>
          <div>
            <h1 style={S.h1}>Zones & Stations</h1>
            <div style={S.sub}>Configure your facility's zones, service stations, counters and patient routing.</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
          <div style={S.search}>
            <Icon name="search" size={16} color="var(--text-muted)" />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search zone, station or counter…" style={S.searchInput} />
          </div>
          <button style={S.primary} onClick={() => setZoneModal("create")}><Icon name="plus" size={16} /> New Zone</button>
        </div>
      </div>

      <div style={S.stats}>
        <Stat icon="pin" tint={P} label="Total Zones" value={totals.zones} />
        <Stat icon="monitor" tint="#0EA5E9" label="Total Stations" value={totals.stations} />
        <Stat icon="users" tint="#7C3AED" label="Total Counters" value={totals.counters} />
        <Stat icon="check" tint={GOOD} label="Active Counters" value={totals.active} />
      </div>

      {loading ? <LoadingSpinner /> : zones.length === 0 ? (
        <EmptyState
          icon="🗂️" title="No zones yet"
          subtitle="Create a zone (e.g. OPD Block) then add stations inside it."
          action={<Btn onClick={() => setZoneModal("create")}>+ Create First Zone</Btn>}
        />
      ) : zones.filter(zoneMatches).map(zone => {
        const zStations = stationsForZone(zone.id);
        const zCounters = zStations.reduce((n, s) => n + countersOf(s).length, 0);
        const open = q ? true : expanded[zone.id];
        return (
          <div key={zone.id} style={S.zoneCard}>
            <div style={S.zoneHead}>
              <button style={S.iconBtn} onClick={() => setExpanded(e => ({ ...e, [zone.id]: !e[zone.id] }))} title={open ? "Collapse" : "Expand"}>
                <Icon name={open ? "chevronDown" : "chevronRight"} size={18} />
              </button>
              <span style={{ ...S.bubble, width: 44, height: 44, background: `${P}1A`, color: P }}><Icon name="building" size={22} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                  <span style={{ fontSize: "19px", fontWeight: 700, color: "var(--text-primary)" }}>{zone.name}</span>
                  <span style={S.pill}>{zStations.length} Station{zStations.length !== 1 ? "s" : ""} · {zCounters} Counter{zCounters !== 1 ? "s" : ""}</span>
                </div>
                {zone.description && <div style={S.muted}>{zone.description}</div>}
              </div>
              <button style={S.ghostBtn} onClick={() => setStationModal({ zoneId: zone.id })}><Icon name="plus" size={14} /> Add Station</button>
              <button style={S.iconBtn} onClick={() => setZoneModal(zone)} title="Edit zone"><Icon name="edit" size={17} /></button>
              <Menu id={`z-${zone.id}`} items={[
                { label: "Add station", icon: "plus", onClick: () => setStationModal({ zoneId: zone.id }) },
                { label: "Edit zone", icon: "edit", onClick: () => setZoneModal(zone) },
                { label: "Delete zone", icon: "trash", danger: true, onClick: () => setDeleteConfirm({ type: "zone", item: zone }) },
              ]} />
            </div>

            {open && (
              <div style={S.zoneBody}>
                {zStations.length === 0 ? (
                  <div style={{ ...S.muted, padding: "14px 6px" }}>No stations yet — use "Add Station".</div>
                ) : zStations.filter(stationMatches).map(st => {
                  const counters = countersOf(st);
                  return (
                    <div key={st.id} style={S.station}>
                      <div style={S.stationHead}>
                        <span style={{ ...S.bubble, width: 42, height: 42, background: `${P}14`, color: P, fontWeight: 700, fontSize: "14px" }}>{st.queue_prefix}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                            <span style={{ fontSize: "16px", fontWeight: 700, color: "var(--text-primary)" }}>{st.name}</span>
                            {st.is_entry_point && <span style={{ ...S.badge, background: `${A}26`, color: "#5E7C00", border: `1px solid ${A}66` }}>Entry point</span>}
                            <span style={S.pill}>{counters.length} Counter{counters.length !== 1 ? "s" : ""}</span>
                          </div>
                          {st.code && <div style={S.muted}>Code {st.code}</div>}
                        </div>
                        <button style={S.ghostBtn} onClick={() => setCounterModal({ stationId: st.id, stationName: st.name })}><Icon name="plus" size={14} /> Add Counter</button>
                        <button style={S.ghostBtn} onClick={() => setStationModal({ ...st, zoneId: zone.id })}><Icon name="edit" size={14} /> Edit Station</button>
                        <Menu id={`s-${st.id}`} items={[
                          { label: "Add outcome", icon: "route", onClick: () => setOutcomeModal({ stationId: st.id, stationName: st.name, allStations: stations }) },
                          { label: "Delete station", icon: "trash", danger: true, onClick: () => setDeleteConfirm({ type: "station", item: st }) },
                        ]} />
                      </div>

                      {counters.length > 0 && (
                        <div style={S.tree}>
                          {counters.filter(c => !q || has(c.name) || has(st.name) || has(zone.name)).map(counter => {
                            const active = counter.is_active !== false;
                            return (
                              <div key={counter.id} style={S.counterRow}>
                                <span style={S.treeLine} />
                                <span style={{ ...S.counterTag, color: P, background: `${P}14` }}>C{counter.number}</span>
                                <span style={{ fontSize: "14px", fontWeight: 600, color: "var(--text-primary)" }}>{counter.name}</span>
                                <span style={{ ...S.badge, background: active ? "#16A34A1F" : "#64748B1F", color: active ? GOOD : "var(--text-secondary)" }}>
                                  <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: active ? GOOD : "#94A3B8", marginRight: 6 }} />
                                  {active ? "Active" : "Inactive"}
                                </span>
                                <span style={{ flex: 1 }} />
                                <button style={S.iconBtn} title="Edit counter" onClick={() => setCounterModal({ ...counter, stationId: st.id, stationName: st.name })}><Icon name="edit" size={16} /></button>
                                <button style={S.iconBtn} title="Move to another station" onClick={() => setMoveModal({ ...counter, currentStationId: st.id, currentStationName: st.name })}><Icon name="swap" size={16} /></button>
                                <button style={{ ...S.iconBtn, color: active ? "#DC2626" : GOOD }} title={active ? "Deactivate" : "Activate"} onClick={() => toggleCounter(counter)}><Icon name="power" size={16} /></button>
                                <button style={S.iconBtn} title="Delete counter" onClick={() => setDeleteConfirm({ type: "counter", item: counter })}><Icon name="trash" size={16} /></button>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <div style={S.outcomes}>
                        <span style={S.outcomesLabel}><Icon name="route" size={14} /> Routing outcomes</span>
                        {(st.outcomes || []).map(o => (
                          <span key={o.id} style={S.chip}>
                            <button style={S.chipMain} title="Edit outcome" onClick={() => setOutcomeModal({ ...o, stationId: st.id, stationName: st.name, allStations: stations })}>
                              {o.label_en}
                              {o.next_station_name && <span style={{ color: "var(--text-muted)" }}> → {o.next_station_name}</span>}
                              {o.is_exit && <span style={{ ...S.tinyBadge, color: GOOD, background: "#16A34A1F" }}>EXIT</span>}
                              {o.is_emergency && <span style={{ ...S.tinyBadge, color: "#DC2626", background: "#DC26261F" }}>EMERGENCY</span>}
                            </button>
                            <button style={S.chipX} title="Delete outcome" onClick={() => setDeleteConfirm({ type: "outcome", item: o })}>×</button>
                          </span>
                        ))}
                        <button style={S.addChip} onClick={() => setOutcomeModal({ stationId: st.id, stationName: st.name, allStations: stations })}>
                          <Icon name="plus" size={13} /> Outcome
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const S = {
  root: { padding: "28px 32px 40px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "1400px" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "16px", flexWrap: "wrap", marginBottom: "22px" },
  h1: { margin: 0, fontSize: "26px", fontWeight: 700, color: "var(--text-primary)" },
  sub: { fontSize: "14px", color: "var(--text-secondary)", marginTop: "3px" },
  search: { display: "flex", alignItems: "center", gap: "8px", background: "var(--surface-bg)", border: "1px solid var(--surface-border)", borderRadius: "10px", padding: "0 12px", height: "42px", minWidth: "260px" },
  searchInput: { border: "none", outline: "none", background: "transparent", color: "var(--text-primary)", fontSize: "13px", flex: 1, fontFamily: "inherit" },
  primary: { display: "inline-flex", alignItems: "center", gap: "8px", background: P, color: "#fff", border: "none", borderRadius: "10px", height: "42px", padding: "0 18px", fontSize: "14px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  stats: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px", marginBottom: "22px" },
  card: { background: "var(--surface-bg)", border: "1px solid var(--surface-border)", borderRadius: "14px" },
  bubble: { display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: "12px", flexShrink: 0 },
  zoneCard: { background: "var(--surface-bg)", border: "1px solid var(--surface-border)", borderRadius: "16px", marginBottom: "16px", overflow: "visible" },
  zoneHead: { display: "flex", alignItems: "center", gap: "12px", padding: "16px 18px", flexWrap: "wrap" },
  zoneBody: { borderTop: "1px solid var(--surface-border)", padding: "10px 18px 16px 62px" },
  station: { padding: "14px 0", borderBottom: "1px solid var(--surface-border)" },
  stationHead: { display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" },
  tree: { margin: "10px 0 4px 20px", display: "flex", flexDirection: "column", gap: "8px" },
  counterRow: { display: "flex", alignItems: "center", gap: "12px", background: "var(--input-bg)", border: "1px solid var(--surface-border)", borderRadius: "10px", padding: "8px 12px", position: "relative", flexWrap: "wrap" },
  treeLine: { position: "absolute", left: "-16px", top: "50%", width: "14px", borderTop: "2px dashed var(--surface-border)" },
  counterTag: { fontSize: "12px", fontWeight: 700, borderRadius: "7px", padding: "3px 9px" },
  outcomes: { display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", margin: "12px 0 0 20px" },
  outcomesLabel: { display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", fontWeight: 700, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginRight: "4px" },
  chip: { display: "inline-flex", alignItems: "center", background: "var(--input-bg)", border: "1px solid var(--surface-border)", borderRadius: "999px", overflow: "hidden" },
  chipMain: { background: "none", border: "none", cursor: "pointer", padding: "5px 6px 5px 12px", fontSize: "13px", fontWeight: 600, color: "var(--text-primary)", fontFamily: "inherit" },
  chipX: { background: "none", border: "none", cursor: "pointer", padding: "5px 10px 5px 4px", fontSize: "15px", color: "var(--text-muted)", fontFamily: "inherit" },
  tinyBadge: { fontSize: "10px", fontWeight: 700, borderRadius: "5px", padding: "2px 6px", marginLeft: "6px" },
  addChip: { display: "inline-flex", alignItems: "center", gap: "5px", background: "transparent", border: `1px dashed ${P}`, color: P, borderRadius: "999px", padding: "5px 12px", fontSize: "13px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  pill: { fontSize: "12px", fontWeight: 600, color: "var(--text-secondary)", background: "var(--input-bg)", border: "1px solid var(--surface-border)", borderRadius: "999px", padding: "3px 12px" },
  badge: { display: "inline-flex", alignItems: "center", fontSize: "12px", fontWeight: 700, borderRadius: "999px", padding: "3px 11px" },
  muted: { fontSize: "13px", color: "var(--text-secondary)", marginTop: "3px" },
  iconBtn: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: "34px", height: "34px", background: "var(--surface-bg)", border: "1px solid var(--surface-border)", borderRadius: "9px", color: "var(--text-secondary)", cursor: "pointer" },
  ghostBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "var(--surface-bg)", border: "1px solid var(--surface-border)", borderRadius: "9px", height: "34px", padding: "0 12px", fontSize: "13px", fontWeight: 600, color: "var(--text-primary)", cursor: "pointer", fontFamily: "inherit" },
  menuBackdrop: { position: "fixed", inset: 0, zIndex: 20 },
  menu: { position: "absolute", right: 0, top: "40px", zIndex: 21, minWidth: "170px", background: "var(--surface-bg)", border: "1px solid var(--surface-border)", borderRadius: "10px", boxShadow: "0 12px 30px rgba(0,0,0,0.18)", padding: "6px", display: "flex", flexDirection: "column" },
  menuItem: { display: "flex", alignItems: "center", gap: "9px", background: "none", border: "none", cursor: "pointer", fontSize: "13px", fontWeight: 600, padding: "9px 10px", borderRadius: "7px", textAlign: "left", fontFamily: "inherit" },
};
